import { useState, useCallback } from 'react';
import { graphQlClient } from '@config/graphqlClient';
import { Crew } from '@models/crew.model';
import CrewsGraphQL from '@graphql/crew.queries';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { SnackType } from '@models/common.model';
import { useTranslation } from '@hooks/useTranslation';

interface UseCrewsReturn {
  crews: Crew[];
  loading: boolean;
  error: string | null;
  fetchCrews: (organizationId: string) => Promise<void>;
}

export const useCrews = (): UseCrewsReturn => {
  const [crews, setCrews] = useState<Crew[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const dispatch = useAppDispatch();
  const localeCommon = useTranslation('common');
  const { userId } = useAppSelector((state) => state.user);

  const fetchCrews = useCallback(async (organizationId: string) => {
    if (!organizationId) {
      setError('Organization ID is required');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data: any = await graphQlClient.request(
        CrewsGraphQL.queries.getCrewsByUser,
        { userId },
      );

      if (data.getCrewsByUser) {
        setCrews(data.getCrewsByUser);
      }
    } catch (e: any) {
      const errorMessage =
        e?.response?.errors[0]?.message || localeCommon['requestError'];
      setError(errorMessage);
      dispatch(
        appendActionMessage({
          message: errorMessage,
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [userId, dispatch, localeCommon]);

  return {
    crews,
    loading,
    error,
    fetchCrews,
  };
};
